import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { UploadCloud, X, Loader2, CheckCircle2 } from "lucide-react";

export const ConsultationModal = ({ open, onClose }) => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", zip_code: "", service_interest: "Staging Consult",
    square_footage: "", target_date: "", notes: "", referral_source: "",
  });
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addFiles = (fileList) => {
    const arr = Array.from(fileList).slice(0, 8 - photos.length);
    setPhotos((p) => [...p, ...arr.map((file) => ({ file, url: URL.createObjectURL(file) }))]);
  };

  const removePhoto = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!form.name || !/\S+@\S+\.\S+/.test(form.email) || !form.phone) {
      toast.error("Please fill in name, a valid email, and phone.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      photos.forEach((p) => fd.append("photos", p.file));
      await api.post("/consultations", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not submit request");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    onClose();
    setTimeout(() => { setDone(false); setPhotos([]); setForm({ name:"",email:"",phone:"",zip_code:"",service_interest:"Staging Consult",square_footage:"",target_date:"",notes:"",referral_source:"" }); }, 250);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="consultation-modal" className="max-w-lg p-0 gap-0 overflow-hidden max-h-[92vh] flex flex-col">
        <DialogHeader className="px-6 pt-6 pb-4 bg-brand-ceruleanDark text-white">
          <DialogTitle className="font-serif text-2xl">Get a Custom Quote & Photo Estimate</DialogTitle>
          <DialogDescription className="text-sm text-white/70 mt-1">For staging, rearrangement, and custom projects. We reply within 1 business day.</DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="p-10 text-center">
            <CheckCircle2 className="w-14 h-14 text-brand-emerald mx-auto mb-4" />
            <h3 className="font-serif text-2xl text-brand-navy">Request Received</h3>
            <p className="text-brand-slate mt-2">Thank you, {form.name.split(" ")[0]}. The owner will review your photos and details and reach out shortly.</p>
            <Button data-testid="consultation-close-btn" onClick={close} className="mt-6 bg-brand-cerulean text-white rounded-full px-8">Done</Button>
          </div>
        ) : (
          <>
            <div className="p-6 overflow-y-auto space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Full name</Label><Input data-testid="consultation-form-name" value={form.name} onChange={(e)=>set("name",e.target.value)} className="mt-1" /></div>
                <div><Label>Phone</Label><Input data-testid="consultation-form-phone" value={form.phone} onChange={(e)=>set("phone",e.target.value)} className="mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email</Label><Input data-testid="consultation-form-email" type="email" value={form.email} onChange={(e)=>set("email",e.target.value)} className="mt-1" /></div>
                <div><Label>Zip code</Label><Input data-testid="consultation-form-zip" value={form.zip_code} onChange={(e)=>set("zip_code",e.target.value)} className="mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Service interest</Label>
                  <select data-testid="consultation-form-interest" value={form.service_interest} onChange={(e)=>set("service_interest",e.target.value)}
                          className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                    <option>Staging Consult</option><option>Interior Rearrangement</option><option>Whole-Home Move</option>
                    <option>Reorganization</option><option>Packing</option><option>Unpacking</option><option>Other / Custom</option>
                  </select>
                </div>
                <div><Label>Sq ft / rooms</Label><Input data-testid="consultation-form-sqft" value={form.square_footage} onChange={(e)=>set("square_footage",e.target.value)} className="mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Target date</Label><Input data-testid="consultation-form-target" type="date" value={form.target_date} onChange={(e)=>set("target_date",e.target.value)} className="mt-1" /></div>
                <div><Label>Realtor referral (optional)</Label><Input data-testid="consultation-form-referral" value={form.referral_source} onChange={(e)=>set("referral_source",e.target.value)} className="mt-1" /></div>
              </div>
              <div><Label>Notes & fragile items</Label><Textarea data-testid="consultation-form-notes" value={form.notes} onChange={(e)=>set("notes",e.target.value)} rows={2} className="mt-1" /></div>

              <div>
                <Label>Project photos (up to 8)</Label>
                <label data-testid="consultation-form-upload"
                       className="mt-1.5 flex flex-col items-center justify-center border-2 border-dashed border-brand-gold/50 rounded-xl py-6 cursor-pointer hover:bg-brand-goldSoft/40 transition-colors">
                  <UploadCloud className="w-7 h-7 text-brand-gold" />
                  <span className="text-sm text-brand-slate mt-1.5">Drag & drop or tap to upload</span>
                  <input type="file" accept="image/*" multiple className="hidden" onChange={(e)=>addFiles(e.target.files)} />
                </label>
                {photos.length > 0 && (
                  <div className="grid grid-cols-4 gap-2 mt-3">
                    {photos.map((p, i) => (
                      <div key={i} className="relative group">
                        <img src={p.url} alt="upload preview" className="w-full h-16 object-cover rounded-lg" />
                        <button onClick={()=>removePhoto(i)} data-testid={`consultation-remove-photo-${i}`}
                                className="absolute -top-1.5 -right-1.5 bg-brand-coral text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-brand-border bg-white">
              <Button data-testid="consultation-form-submit-btn" disabled={submitting} onClick={submit}
                      className="w-full bg-brand-gold hover:bg-brand-goldHover text-white rounded-full">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</> : "Submit Request"}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
